import '@mantine/carousel/styles.css';

import { Head, Link, usePage } from '@inertiajs/react';
import { Carousel } from '@mantine/carousel';
import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  GridCol,
  Group,
  Image,
  Modal,
  Paper,
  rem,
  Space,
  Stack,
  Tabs,
  Text,
  Textarea,
  Title,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure, useIntersection, useMediaQuery } from '@mantine/hooks';
import { IconArtboard, IconCube3dSphere, IconInfoCircle, IconShieldCheck, IconTrash } from '@tabler/icons-react';
import djs from 'dayjs/esm/index.js';
import relativeTime from 'dayjs/plugin/relativeTime';
import React from 'react';

import { MoneyFormatter } from '@/components/MoneyFormatter.jsx';
import OfferProgress from '@/components/OfferProgress.jsx';
import useCountdown from '@/components/useCountdown.jsx';
import useInertiaForm from '@/components/useInertiaForm.jsx';
import { createSiteLayout } from '@/pages/site/layouts/SiteLayout.jsx';

import LimitedEditionIcon from './limited-edition.svg';
import classes from './Offer.module.css';

djs.extend(relativeTime);

function Hero({ offer }) {
  const isNarrowHeight = useMediaQuery('(max-height: 800px)');

  return (
    <Box>
      <Container ta={'center'} py={'xl'}>
        <Title fw={400} fz={rem(isNarrowHeight ? 45 : 64)} c="gfdark.9" lts={-2}>
          {offer.artwork.name}
        </Title>
        <Text fw={300} fz={rem(24)} mt={0} c="gfdark.5" lts={-1}>
          {offer.artwork.artist.name}
        </Text>
      </Container>
    </Box>
  );
}

function InvestButton({ offer, size = 'lg', val, ...props }) {
  const isRegistered = !!usePage().props?.auth?.user;

  if (!isRegistered) {
    return (
      <Button fullWidth size={size} component={Link} href={route('login')}>
        LOGIN TO VIEW
      </Button>
    );
  }

  return (
    <Group p={'sm'} justify={'center'} {...props}>
      {offer.status_action === 'COMING SOON' && (
        <Button fullWidth size={size}>
          {offer.status_action}
        </Button>
      )}
      {offer.status_action === 'BUY' && (
        <Button fullWidth size={size} component={Link} href={route('site.invest', [offer.id, { val: val }])}>
          {offer.status_action}
        </Button>
      )}
      {offer.status_action === 'SOLD OUT!' && (
        <Button fullWidth size={size} variant={'outline'} color={'green'}>
          {offer.status_action}
        </Button>
      )}
    </Group>
  );
}

function HeroOffer({ offer }) {
  // const isNarrowHeight = useMediaQuery('(max-height: 800px)');
  // const imageHeight = isNarrowHeight ? 355 : 500;
  const imageHeight = 500;

  // Story modal
  const [storyOpened, storyHandlers] = useDisclosure(false);

  const slides = offer?.artwork?.images.map((file, index) => (
    <Carousel.Slide key={index}>
      <Image height={imageHeight} src={file.url} fit={'contain'} />
    </Carousel.Slide>
  ));

  const videoSlides = offer?.artwork?.videos.map((document, index) =>
    document.original_name.includes('.mp4') ? (
      <Carousel.Slide key={index}>
        {/*<Image height={imageHeight} src={document.url} fit={'contain'} />*/}
        <video width={'100%'} height={imageHeight} controls>
          <source src={document.url + '#t=0.001'} type="video/mp4" />
          Your browser does not support the videos.
        </video>
      </Carousel.Slide>
    ) : null,
  );

  const isRegistered = !!usePage().props?.auth?.user;

  return (
    <Box py={'md'} pb={'xl'} mb={'xl'} pt={0}>
      <Modal
        opened={storyOpened}
        onClose={storyHandlers.close}
        size={'auto'}
        centered
        transitionProps={{ transition: 'fade', duration: 200 }}
      >
        <video style={{ maxHeight: '80vh', maxWidth: '80vw' }} loop autoPlay playsInline controls>
          <source src={offer?.artwork?.artist?.videos[0]?.url + '#t=0.001'} type="video/mp4" />
          Your browser does not support the videos.
        </video>
      </Modal>

      <Grid align={'stretch'} justify="space-between" gutter={'xl'}>
        <GridCol span={{ md: 8 }}>
          <Carousel
            classNames={classes}
            slideSize={'100%'}
            loop
            height={imageHeight}
            className={'w-border'}
            withIndicators={true}
            withControls={true}
            slideGap={'md'}
          >
            {slides}
            {videoSlides}
          </Carousel>

          <OfferProgress offer={offer} ta={'left'} mt={'xl'} p={'xl'} className="w-border" />
        </GridCol>
        <GridCol span={'auto'}>
          <Box h={'100%'} ta={'left'} p={'xl'} className="w-border">
            <Stack justify={'space-between'} h={'100%'}>
              {/* Header */}
              <Box>
                {/* Artist Info */}
                <Group gap={'sm'}>
                  {offer?.artwork?.artist?.images?.length && (
                    <Box style={{ position: 'relative' }}>
                      <Avatar
                        src={offer?.artwork?.artist?.images[0]?.url}
                        className={offer?.artwork?.artist?.videos?.length ? 'border-gradient-rounded' : ''}
                        onClick={() => {
                          if (!offer?.artwork?.artist?.videos?.length) {
                            return;
                          }
                          storyHandlers.open();
                        }}
                      />
                    </Box>
                  )}
                  <Box>
                    <Text fz={rem(12)} fw={'bold'} c="gfdark.5">
                      ARTIST
                    </Text>
                    <Text fz={rem(16)} fw={'lighter'} c="gfdark.5">
                      {offer.artwork.artist.name}
                    </Text>
                  </Box>
                </Group>

                {/* Offer Info */}
                <Text fz={rem(20)} c="gfdark.9" mt={'lg'} lts={-1}>
                  {offer.artwork.name}
                </Text>
                <Text fz={rem(14)} fw={'lighter'} c="gfdark.5">
                  {offer.artwork.medium}
                </Text>

                {/* Insured & Protected info */}
                <a target="_blank" href={route('site.offer.insurance', [offer.id, '-'])} rel="noreferrer">
                  <Group mt={'lg'} justify={'space-between'} align={'center'}>
                    <Group>
                      <IconShieldCheck size={'1.5rem'} stroke={2} color="var(--mantine-color-green-5)" />
                      <Text fz={rem(16)} fw={700} c={'gfdark.9'}>
                        INSURED & PROTECTED
                      </Text>
                    </Group>
                    <Tooltip
                      multiline
                      w={220}
                      label="This original Islamic artwork is fully insured against loss, damage, and transit risks by a certified art policy."
                    >
                      <IconInfoCircle size={'1.5rem'} stroke={1.5} color="var(--mantine-color-gfdark-5)" />
                    </Tooltip>
                  </Group>
                </a>
              </Box>

              {/* Offer Details */}
              <Box>
                {/* --- */}
                <Group bg={'gf'} p={'sm'} justify={'space-between'}>
                  <Text fz={rem(12)} fw={'bold'} c="white" truncate>
                    INITIAL SALES PRICE
                  </Text>
                  <Text fz={rem(20)} c="white" truncate>
                    {isRegistered ? <MoneyFormatter value={offer.price} prefix={offer.price_currency} /> : '$***'}
                  </Text>
                </Group>
                {/* --- */}
                <Group p={'sm'} justify={'space-between'}>
                  <Text fz={rem(12)} fw={'bold'} c="gfdark.5" truncate>
                    TOTAL SHARES
                  </Text>
                  <Text fz={rem(20)} c="gfdark.5" truncate>
                    {isRegistered ? offer.quantity : '***'}
                  </Text>
                </Group>
                {/* --- */}
                {offer.status === 'published' && (
                  <Group p={'sm'} justify={'space-between'}>
                    <Text fz={rem(12)} fw={'bold'} c="gfdark.5" truncate>
                      NUMBER OF SHARES AVAILABLE
                    </Text>
                    <Text fz={rem(20)} c="gfdark.5" truncate>
                      {isRegistered ? offer.quantity_available : '***'}
                    </Text>
                  </Group>
                )}
                {/* --- */}
                <Group p={'sm'} justify={'space-between'}>
                  <Text fz={rem(12)} fw={'bold'} c="gfdark.5" truncate>
                    PRICE PER SHARE
                  </Text>
                  <Text fz={rem(20)} c="gfdark.5" truncate>
                    <MoneyFormatter value={offer.price_per_share} prefix={offer.price_currency} />
                  </Text>
                </Group>
                {/* --- */}
                {offer?.active_discount && offer.status_action !== 'SOLD OUT!' && (
                  <Stack bg={'gf'} p={'sm'} align={'center'} gap={3}>
                    <Text fz={rem(18)} fw={'bold'} c="white" truncate>
                      LIMITED TIME OFFER
                    </Text>
                    {offer.active_discount.type === 'Extra Shares' && (
                      <>
                        <Text fz={rem(14)} fw={'bold'} c="white" truncate>
                          YOU WILL GET {offer.active_discount.value}% EXTRA SHARES
                        </Text>
                        <Text tt={'uppercase'} fz={rem(16)} fw={'bold'} c="white" truncate>
                          <DiscountTimer activeDiscount={offer.active_discount} />
                        </Text>
                      </>
                    )}
                  </Stack>
                )}
                {/* --- */}
                <InvestButton offer={offer} />
              </Box>
            </Stack>
          </Box>
        </GridCol>
      </Grid>
    </Box>
  );
}

function DiscountTimer({ activeDiscount }) {
  const expireDate = activeDiscount.dates[1];

  const remainedTime = expireDate ? Math.max(djs(expireDate).diff(djs(), 'second'), 0) : 0;
  if (remainedTime < 60 * 5) {
    const timeLeft = useCountdown(remainedTime);
    return timeLeft + ' seconds left';
  }

  return djs(expireDate).fromNow(true) + ' left';
}

function Cataloguing({ offer }) {
  return (
    <>
      <Title order={2} c={'gfdark.9'} fz={rem(32)} lts={-1}>
        Cataloguing
      </Title>

      <Box
        mt={'lg'}
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '10px',
          alignItems: 'center',
        }}
      >
        <Text c={'gfdark.9'} tt={'uppercase'} fw={'bolder'} fz={rem(12)}>
          TITLE
        </Text>
        <Text pl={'lg'} c={'gfdark.5'} fw={'lighter'} fz={rem(16)}>
          {offer?.artwork?.name}
        </Text>

        <Text c={'gfdark.9'} tt={'uppercase'} fw={'bolder'} fz={rem(12)}>
          ARTIST
        </Text>
        <Text pl={'lg'} c={'gfdark.5'} fw={'lighter'} fz={rem(16)}>
          {offer?.artwork?.artist?.name}
        </Text>

        <Text c={'gfdark.9'} tt={'uppercase'} fw={'bolder'} fz={rem(12)}>
          MEDIUM
        </Text>
        <Text pl={'lg'} c={'gfdark.5'} fw={'lighter'} fz={rem(16)} style={{ wordWrap: 'break-word' }} maw={'70vw'}>
          {offer?.artwork?.medium}
        </Text>

        <Text c={'gfdark.9'} tt={'uppercase'} fw={'bolder'} fz={rem(12)}>
          YEAR
        </Text>
        <Text pl={'lg'} c={'gfdark.5'} fw={'lighter'} fz={rem(16)}>
          {offer?.artwork?.year}
        </Text>

        {offer?.artwork?.cataloguing?.map((item, index) => (
          <React.Fragment key={index}>
            <Text c={'gfdark.9'} tt={'uppercase'} fw={'bolder'} fz={rem(12)}>
              {item.name === 'Solana Address' ? 'Contract Address' : item.name}
            </Text>
            {item.name === 'Solana Address' ? (
              <Group gap={'xs'} pl={'lg'}>
                <Tooltip
                  multiline
                  w={220}
                  label="This is the contract address of the representative tokens for the artworks listed on Goldframer. Each token symbolizes a fractional ownership share of this unique art piece."
                >
                  <IconInfoCircle size={'1.5rem'} stroke={1.5} color="var(--mantine-color-gfdark-5)" />
                </Tooltip>
                <Text c={'gfdark.5'} fw={'lighter'} fz={rem(16)} style={{ wordWrap: 'break-word' }} maw={'30vw'} truncate="end">
                  <a
                    href={`https://explorer.solana.com/address/${item.value}?cluster=mainnet-beta`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {item.value}
                  </a>
                </Text>
              </Group>
            ) : (
              <Text pl={'lg'} c={'gfdark.5'} fw={'lighter'} fz={rem(16)}>
                {item.value}
              </Text>
            )}
          </React.Fragment>
        ))}
      </Box>
    </>
  );
}

export default function Offer({ offer }) {
  const refs = {
    documents: useIntersection({ threshold: 1 }),
    exhibition: useIntersection({ threshold: 1 }),
    provenance: useIntersection({ threshold: 1 }),
    cataloguing: useIntersection({ threshold: 1 }),
    about: useIntersection({ threshold: 1 }),
    rewards: useIntersection({ threshold: 1 }),
    comments: useIntersection({ threshold: 1 }),
  };

  // Get active tab based on intersection
  const activeTab = Object.entries(refs).find(([_, { entry }]) => entry?.isIntersecting)?.[0] || null;

  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const hasRewards = offer.artwork.rewards?.length > 0;

  return (
    <>
      <Head>
        <title>{offer.artwork.name}</title>
      </Head>
      <Container>
        <Hero offer={offer} />
        <HeroOffer offer={offer} />

        {/* marketplace & fine print link */}
        <Grid align={'stretch'} gutter={'xl'} mb={'xl'}>
          {/* marketplace */}
          {offer?.artwork?.marketplace_status === 'available' && (
            <GridCol span={{ base: 12, md: offer?.artwork?.fine_print_link ? 6 : 12 }}>
              <Box className="w-border" p={'xl'} h={'100%'} style={{ position: 'relative' }}>
                <Group align={'center'} wrap={isMobile ? 'wrap' : 'nowrap'} gap={'xl'} px={isMobile ? 'xs' : 'lg'} h={'100%'}>
                  <IconCube3dSphere size={70} stroke={1.25} color={theme.colors.gf[5]} style={{ flexShrink: 0 }} />
                  <Stack gap={0} align={'flex-start'}>
                    <Text lts={-1} fw={'bolder'} fz={rem(20)} c={'gfdark.9'}>
                      Buy In The Marketplace
                    </Text>
                    <Text lts={-1} fw={400} c={'gfdark.7'} fz={rem(18)} maw={'400px'}>
                      Access a secondary market where collectors list, discover, and trade artwork shares.
                    </Text>
                    <Button
                      component={Link}
                      href={route('site.marketplace', { artwork: offer.artwork.id })}
                      variant="filled"
                      size="md"
                      c={'white'}
                      mt={'lg'}
                      w={isMobile ? null : 245}
                      fullWidth={isMobile}
                    >
                      VISIT MARKETPLACE
                    </Button>
                  </Stack>
                </Group>
              </Box>
            </GridCol>
          )}

          {/* fine print link */}
          {offer?.artwork?.fine_print_link && (
            <GridCol span={{ base: 12, md: offer?.artwork?.marketplace_status === 'available' ? 6 : 12 }}>
              <Box className="w-border" p={'xl'} h={'100%'} style={{ position: 'relative' }}>
                <Group align={'center'} wrap={isMobile ? 'wrap' : 'nowrap'} gap={'xl'} px={isMobile ? 'xs' : 'lg'} h={'100%'}>
                  <IconArtboard size={70} stroke={1.25} color={theme.colors.gf[5]} style={{ flexShrink: 0 }} />
                  <Stack gap={0} align={'flex-start'}>
                    <Text lts={-1} fw={'bolder'} fz={rem(20)} c={'gfdark.9'}>
                      Buy The Fine Print of This Artwork
                    </Text>
                    <Text lts={-1} fw={400} c={'gfdark.7'} fz={rem(18)} maw={'400px'}>
                      Own a copy of certified and artist-signed physical edition of an investment-grade piece.
                    </Text>
                    <Button
                      component={'a'}
                      href={offer?.artwork?.fine_print_link}
                      variant="filled"
                      size="md"
                      c={'white'}
                      mt={'lg'}
                      target={'_blank'}
                      w={isMobile ? null : 245}
                      fullWidth={isMobile}
                    >
                      BUY ON GOLDFRAMER SHOP
                    </Button>
                  </Stack>
                </Group>
              </Box>
            </GridCol>
          )}
        </Grid>

        <Grid align={'stretch'} justify="space-between" gutter={'xl'}>
          <GridCol span={{ md: hasRewards ? 8 : 12 }}>
            <Tabs
              color="gf"
              value={activeTab}
              onChange={tab => {
                window.scrollTo({ top: refs[tab].entry.target.offsetTop - 100, behavior: 'smooth' });
              }}
            >
              {/* Tabs */}
              <Tabs.List
                grow
                style={{ position: !isMobile ? 'sticky' : 'relative', top: !isMobile ? 60 : 0, background: 'white' }}
              >
                <Tabs.Tab value="about">ABOUT</Tabs.Tab>
                {offer?.artwork?.cataloguing?.length ? <Tabs.Tab value="cataloguing">CATALOGUING</Tabs.Tab> : null}
                {offer?.artwork?.provenance ? <Tabs.Tab value="provenance">PROVENANCE</Tabs.Tab> : null}
                {offer?.artwork?.exibition ? <Tabs.Tab value="exhibition">EXHIBITION HISTORY</Tabs.Tab> : null}
                {hasRewards && isMobile ? <Tabs.Tab value="rewards">PACKAGES</Tabs.Tab> : null}
                {offer?.artwork?.documents?.length ? <Tabs.Tab value="documents">DOCUMENTS</Tabs.Tab> : null}
                <Tabs.Tab value="comments">COMMENTS</Tabs.Tab>
              </Tabs.List>

              {/* About */}
              <Box p={'lg'} ref={refs.about.ref}>
                <Title order={2} c={'gfdark.9'} fz={rem(32)} lts={-1}>
                  About
                </Title>
                <Text fz={rem(16)} c={'gfdark.9'} dangerouslySetInnerHTML={{ __html: offer.artwork.description || '-' }}></Text>
              </Box>

              {/* Cataloguing */}
              {!offer?.artwork?.cataloguing?.length ? null : (
                <Box p={'lg'} ref={refs.cataloguing.ref}>
                  <Cataloguing offer={offer} />
                </Box>
              )}

              {/* Provenance */}
              {!offer?.artwork?.provenance ? null : (
                <Box p={'lg'} ref={refs.provenance.ref}>
                  <Title order={2} c={'gfdark.9'} fz={rem(32)} lts={-1}>
                    Provenance
                  </Title>

                  <Text fz={rem(16)} c={'gfdark.9'} dangerouslySetInnerHTML={{ __html: offer.artwork.provenance }}></Text>
                </Box>
              )}

              {/* Exhibition */}
              {!offer?.artwork?.exibition ? null : (
                <Box p={'lg'} ref={refs.exhibition.ref}>
                  <Title order={2} c={'gfdark.9'} fz={rem(32)}>
                    Exhibition History
                  </Title>

                  <Text fz={rem(16)} c={'gfdark.9'} dangerouslySetInnerHTML={{ __html: offer.artwork.exibition }}></Text>
                </Box>
              )}

              {/* Rewards */}
              {hasRewards && isMobile ? (
                <Box p={'lg'} ref={refs.rewards.ref}>
                  <Rewards offer={offer} />
                </Box>
              ) : null}

              {/* Documents */}
              {!offer?.artwork?.documents?.length ? null : (
                <Box p={'lg'} ref={refs.documents.ref}>
                  <Title order={2} c={'gfdark.9'} fz={rem(32)} lts={-1}>
                    Documents
                  </Title>

                  <Stack gap={'lg'}>
                    {offer.artwork.documents.map((document, index) => (
                      <Group key={index} justify={'space-between'} align={'center'}>
                        <a target="_blank" href={document.url} rel="noreferrer">
                          <Text fz={rem(16)}>{document.original_name}</Text>
                        </a>
                      </Group>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Comments */}
              <Box p={'lg'} ref={refs.comments.ref}>
                <Title order={2} c={'gfdark.9'} fz={rem(32)} lts={-1}>
                  Comments
                </Title>
              </Box>

              <Comments />

              <Space h={'xl'} />
            </Tabs>
          </GridCol>

          {/* Rewards */}
          {hasRewards && !isMobile && (
            <GridCol span={'auto'}>
              <Rewards offer={offer} />
            </GridCol>
          )}
        </Grid>
      </Container>
    </>
  );
}

function Comments() {
  const comments = usePage().props.comments;
  const errors = usePage().props.errors;

  const form = useInertiaForm(
    {
      initialValues: {
        content: '',
        commentable_id: usePage().props.offer.id,
        commentable_type: 'offer',
      },
      validate: {
        content: value => (value ? null : 'This field is required'),
      },
    },
    errors,
    route('site.comment', usePage().props.offer.id),
    'Thank you for your comment! It will be visible after moderation.',
  );

  return (
    <Stack gap={'lg'}>
      <Stack align={'flex-start'} justify={'flex-start'} gap={'xs'}>
        <Textarea w={'100%'} placeholder={'Write your comment here...'} {...form.getInputProps('content')} />
        <Stack justify={'flex-end'} gap={0} c={'dimmed'}>
          <Text size="xs">Your comment will be visible to everyone.</Text>
          <Text size="xs">Please be respectful and follow our community guidelines.</Text>
        </Stack>
        <Button size="sm" onClick={form.handleSubmit}>
          Submit
        </Button>
      </Stack>

      {comments?.length === 0 && <Text size="lg">No comments yet. Be the first to comment!</Text>}

      {comments?.map((comment, index) => (
        <Paper key={index} withBorder shadow="0" p={'lg'} opacity={comment.is_approved ? 1 : 0.5}>
          <Group>
            <Avatar size="md">{comment.author_initials}</Avatar>
            <Stack gap={0}>
              <Text size="lg" lh={1}>
                {comment.author_name}
              </Text>
              <Text size="xs" c={'dimmed'}>
                {djs(comment.created_at).format('MMMM D, YYYY HH:mm:ss')}
              </Text>
            </Stack>
            {comment.is_author && (
              <ActionIcon
                component={Link}
                method={'post'}
                preserveScroll={true}
                href={route('site.comment.delete', { id: comment.id })}
                onClick={e => !confirm('Are you sure you want to delete?') && e.preventDefault()}
                size="md"
                color="red"
                variant="light"
              >
                <IconTrash style={{ width: '70%', height: '70%' }} stroke={1.5} />
              </ActionIcon>
            )}
          </Group>
          <Text mt={'xs'} pt={0}>
            {comment.content}
          </Text>
        </Paper>
      ))}
    </Stack>
  );
}

function Rewards({ offer, ...props }) {
  // const theme = useMantineTheme();
  // const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);

  return (
    <Box h={'100%'} ta={'left'} mb={'xl'} {...props}>
      <Title order={3} fw={500} c={'gfdark.9'} ta={'left'} fz={rem(32)} lts={-1}>
        Available Packages
      </Title>
      {offer.artwork.rewards?.map((reward, index) => (
        <Box key={index} mt={'lg'} className="w-border" p={'xl'}>
          <Text fz={rem(20)} c={'gfdark.9'}>
            {reward.title}
          </Text>
          <Text fz={rem(12)} c={'gfdark.9'}>
            Minimum Investment: <MoneyFormatter value={reward.value} prefix={offer.price_currency} />
          </Text>
          <Text mt={'sm'} fz={rem(16)} c={'gfdark.9'}>
            {reward.desc}
          </Text>

          <InvestButton p={0} mt={'md'} size="md" offer={offer} val={reward.value} />
        </Box>
      ))}
    </Box>
  );
}

Offer.layout = page => createSiteLayout(page);
